import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenSearchIndexPicker from "../OpenSearchIndexPicker";
import IndexTimeFieldAutocomplete from "../IndexTimeFieldAutocomplete";
import { AppFormField, AppFormGrid, PageSectionCard } from "../layout";
import { GRID_HALF } from "../../lib/ui/formLayout";
import type { WidgetDataSourceEntry } from "../../types/widget";
import { mergeSourcesByIndices } from "../../utils/widgetDataSources";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  sources: WidgetDataSourceEntry[];
  onChange: (sources: WidgetDataSourceEntry[]) => void;
  disabled?: boolean;
};

export default function WidgetDataSourcesEditor({ sources, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;
  const selectedIndices = sources.map((s) => s.index);

  return (
    <Stack spacing={2}>
      <OpenSearchIndexPicker
        value={selectedIndices}
        onChange={(indices) =>
          onChange(mergeSourcesByIndices(sources, indices, sources[0]?.timeField || ""))
        }
        label={t("widget.indices")}
        helperText={t("widget.indicesHelp")}
        disabled={disabled}
        size="medium"
      />

      {sources.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {t("widget.sourcesTimeFieldHint")}
          </Typography>
          {sources.map((source, index) => (
            <PageSectionCard key={source.index} noPadding>
              <AppFormGrid spacing="compact" alignItems="center" sx={styles.dataSourcesGrid}>
                <AppFormField size={GRID_HALF}>
                  <Typography variant="body2" sx={styles.dataSourcesIndexLabel}>
                    {source.index}
                  </Typography>
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <IndexTimeFieldAutocomplete
                    index={source.index}
                    value={source.timeField}
                    onChange={(timeField) => {
                      const next = sources.map((item, i) =>
                        i === index ? { ...item, timeField } : item
                      );
                      onChange(next);
                    }}
                    disabled={disabled}
                  />
                </AppFormField>
              </AppFormGrid>
            </PageSectionCard>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
